// openEHR AM model — BMM package org.openehr.am.aom2.constraint_model.primitive
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { T } from "../../_shared.ts";
import { C_PRIMITIVE } from "../../_unassigned.ts";
import { C_PRIMITIVE_OBJECT } from "../constraint_model.ts";
import * as openehr_base from "../../../base/mod.ts";

/**
 * Constraint on instances of Boolean. Both attributes cannot be set to False, since this would mean that the Boolean value being constrained cannot be True or False.
 */
export class C_BOOLEAN extends C_PRIMITIVE {
  /**
   * Internal storage for true_valid
   * @protected
   */
  protected _true_valid?: openehr_base.Boolean;

  /**
   * True if the value True is allowed.
   */
  get true_valid(): boolean | undefined {
    return this._true_valid?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for true_valid.
   * Use this to access openehr_base.Boolean methods.
   */
  get $true_valid(): openehr_base.Boolean | undefined {
    return this._true_valid;
  }

  /**
   * Sets true_valid from either a primitive value or openehr_base.Boolean wrapper.
   */
  set true_valid(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._true_valid = undefined;
    } else if (typeof val === "boolean") {
      this._true_valid = openehr_base.Boolean.from(val);
    } else {
      this._true_valid = val;
    }
  }

  /**
   * Internal storage for false_valid
   * @protected
   */
  protected _false_valid?: openehr_base.Boolean;

  /**
   * True if the value False is allowed.
   */
  get false_valid(): boolean | undefined {
    return this._false_valid?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for false_valid.
   * Use this to access openehr_base.Boolean methods.
   */
  get $false_valid(): openehr_base.Boolean | undefined {
    return this._false_valid;
  }

  /**
   * Sets false_valid from either a primitive value or openehr_base.Boolean wrapper.
   */
  set false_valid(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._false_valid = undefined;
    } else if (typeof val === "boolean") {
      this._false_valid = openehr_base.Boolean.from(val);
    } else {
      this._false_valid = val;
    }
  }

  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Boolean = undefined;
}

/**
 * Constraint on instances of STRING.
 */
export class C_STRING extends C_PRIMITIVE {
  /**
   * Internal storage for pattern
   * @protected
   */
  protected _pattern?: openehr_base.String;

  /**
   * Regular expression pattern for proposed instances of String to match.
   */
  get pattern(): string | undefined {
    return this._pattern?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for pattern.
   * Use this to access openehr_base.String methods.
   */
  get $pattern(): openehr_base.String | undefined {
    return this._pattern;
  }

  /**
   * Sets pattern from either a primitive value or openehr_base.String wrapper.
   */
  set pattern(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._pattern = undefined;
    } else if (typeof val === "string") {
      this._pattern = openehr_base.String.from(val);
    } else {
      this._pattern = val;
    }
  }

  /**
   * Set of Strings specifying constraint.
   */
  list?: undefined;
  /**
   * Internal storage for list_open
   * @protected
   */
  protected _list_open?: openehr_base.Boolean;

  /**
   * True if the list is being used to specify the constraint but is not considered exhaustive.
   */
  get list_open(): boolean | undefined {
    return this._list_open?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for list_open.
   * Use this to access openehr_base.Boolean methods.
   */
  get $list_open(): openehr_base.Boolean | undefined {
    return this._list_open;
  }

  /**
   * Sets list_open from either a primitive value or openehr_base.Boolean wrapper.
   */
  set list_open(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._list_open = undefined;
    } else if (typeof val === "boolean") {
      this._list_open = openehr_base.Boolean.from(val);
    } else {
      this._list_open = val;
    }
  }

  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.String = undefined;
  /**
   * True if a_value is valid with respect to constraint expressed in concrete instance of this type.
   * @param a_value - Parameter
   * @returns Result value
   */
  valid_value(a_value: openehr_base.String): openehr_base.Boolean {
    // TODO: Implement valid_value behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method valid_value not yet implemented.");
  }
}

/**
 * Constraint on instances of Integer.
 */
export class C_INTEGER extends C_PRIMITIVE {
  /**
   * Set of Integers specifying constraint.
   */
  list?: undefined;
  /**
   * Range of Integers specifying constraint.
   */
  range?: undefined;
  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Integer = undefined;
}

/**
 * Constraint on instances of Real.
 */
export class C_REAL extends C_PRIMITIVE {
  /**
   * Set of Reals specifying constraint.
   */
  list?: undefined;
  /**
   * Range of Real specifying constraint.
   */
  range?: undefined;
  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Any = undefined;
}

/**
 * Abstract parent of primitive constrainer classes based on \`Ordered\` base types, i.e. types like \`Integer\`, \`Real\`, and the Date/Time types. The model constraint is a List of Intervals, which may include point Intervals, and acts as a efficient and formally tractable representation of any number of point values and/or contiguous intervals of an ordered value domain.
 *
 * In its simplest form, the constraint accessor returns just a single point \`Interval<T>\` object, representing a single value.
 *
 * The next simplest form is a single proper \`Interval <T>\` (i.e. normal two-sided or half-open interval). The most complex form is a list of any combination of point and proper intervals.
 */
export abstract class C_ORDERED<T extends openehr_base.Ordered>
  extends C_PRIMITIVE_OBJECT {
  /**
   * Constraint in the form of a List of Intervals of the parameter type T. Concrete types generated in descendants via template binding.
   */
  constraint?: undefined;
  /**
   * Default value set in a template, and present in an operational template. Generally limited to leaf and near-leaf nodes.
   */
  default_value?: T;
  /**
   * Value to be assumed if none sent in data.
   */
  override assumed_value?: T = undefined;
  /**
   * True if any value (i.e. instance) of the reference model type would be allowed. Redefined in descendants.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    // TODO: Implement any_allowed behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method any_allowed not yet implemented.");
  }

  /**
   * True if \`_other.any_allowed_\` or else for every constraint in the \`_constraint_\` list there is a constraint in \`_other.constraint_\` that contains it.
   * @param other - Parameter
   * @returns Result value
   */
  c_value_conforms_to(_other: C_ORDERED<T>): openehr_base.Boolean {
    // TODO: Implement c_value_conforms_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_value_conforms_to not yet implemented.");
  }

  /**
   * True if the items in \`_constraint_\` are equal in number and identical pair-wise with those in \`_other.constraint_\`.
   * @param other - Parameter
   * @returns Result value
   */
  c_value_congruent_to(_other: C_ORDERED<T>): openehr_base.Boolean {
    // TODO: Implement c_value_congruent_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_value_congruent_to not yet implemented.");
  }
}

/**
 * Constrainer type for instances of \`Terminology_code\`. The constraint attribute can contain:
 *
 * * a single at-code
 * * a single ac-code, representing a value-set that is defined in the archetype terminology
 *
 * If there is an assumed value for the ac-code case above, the \`_assumed_value_\` attribute contains a single at-code, which must come from the list of at-codes defined as the internal value set for the ac-code.
 *
 * The \`_constraint_status_\` attribute and \`_constraint_required_()\` function together define whether the \`_constraint_\` is considered formal ('required') or not. In the non-required cases, a data-item matched to this constraint may be any coded term.
 */
export class C_TERMINOLOGY_CODE extends C_PRIMITIVE_OBJECT {
  /**
   * Internal storage for constraint
   * @protected
   */
  protected _constraint?: openehr_base.String;

  /**
   * Type of individual constraint - a single string that can either be a local at-code, or a local ac-code signifying a locally defined value set. If an ac-code, assumed_value may contain an at-code from the value set of the ac-code.
   *
   * Use an empty string for no constraint.
   */
  get constraint(): string | undefined {
    return this._constraint?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for constraint.
   * Use this to access openehr_base.String methods.
   */
  get $constraint(): openehr_base.String | undefined {
    return this._constraint;
  }

  /**
   * Sets constraint from either a primitive value or openehr_base.String wrapper.
   */
  set constraint(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._constraint = undefined;
    } else if (typeof val === "string") {
      this._constraint = openehr_base.String.from(val);
    } else {
      this._constraint = val;
    }
  }

  /**
   * Assumed Terminology code value.
   */
  override assumed_value?: openehr_base.Terminology_code = undefined;
  default_value?: openehr_base.Terminology_code;
  /**
   * Constraint status of this terminology constraint. If Void, the meaning is as follows:
   *
   * * in a top-level  archetype, equivalent to \`required\`;
   * * in a specialised (source) archetype, the meaning is to inherit the value from the corresponding node in the parent.
   *
   * In the case of a specialised archetype generated by flattening, the value of this field will be:
   *
   * * Void if it was Void in the parent;
   * * otherwise, it will carry the same value as in the parent.
   */
  constraint_status?: CONSTRAINT_STATUS;
  /**
   * True if \`_constraint_status_\` is defined and equals \`required\` OR if Void. I.e. in archetypes where \`C_TERMINOLOGY_CODE\` instances have no \`_constraint_status_\`, the \`required\` status is assumed, which applies to all legacy archetypes.
   * @returns Result value
   */
  constraint_required(): openehr_base.Boolean {
    // TODO: Implement constraint_required behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method constraint_required not yet implemented.");
  }

  /**
   * Return the effective integer value of the \`_constraint_status_\` field if it exists. If it is null, return 0, i.e. \`required\`.
   *
   * NOTE: the above logic applies to any \`C_TERMINOLOGY_NODE\` instance in a specialised archetype that redefines another such instance in the flat parent. I.e. no stated \`_constraint_status_\` means \`required\`.
   * @returns Result value
   */
  effective_constraint_status(): openehr_base.Integer {
    // TODO: Implement effective_constraint_status behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method effective_constraint_status not yet implemented.");
  }

  /**
   * Effective set of at-code values corresponding to an ac-code for a locally defined value set. Not defined for ac-codes that have no local value set.
   * @returns Result value
   */
  value_set_expanded(): openehr_base.String {
    // TODO: Implement value_set_expanded behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method value_set_expanded not yet implemented.");
  }

  /**
   * For locally defined value sets within individual code bindings: return the term URI(s) substituted from bindings for local at-codes in \`_value_set_expanded_\`.
   * @returns Result value
   */
  value_set_substituted(): openehr_base.Uri {
    // TODO: Implement value_set_substituted behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method value_set_substituted not yet implemented.");
  }

  /**
   * For locally defined value sets within individual code bindings: final set of external codes to which value set is resolved.
   * @returns Result value
   */
  value_set_resolved(): openehr_base.Terminology_code {
    // TODO: Implement value_set_resolved behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method value_set_resolved not yet implemented.");
  }

  /**
   * True if a \`_value_\` is valid with respect to constraint expressed in concrete instance of this type.
   * @param a_value - Parameter
   * @returns Result value
   */
  valid_value(a_value: openehr_base.Terminology_code): openehr_base.Boolean {
    // TODO: Implement valid_value behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method valid_value not yet implemented.");
  }

  /**
   * A generated prototype value from this constraint object.
   * @returns Result value
   */
  prototype_value(): openehr_base.Terminology_code {
    // TODO: Implement prototype_value behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method prototype_value not yet implemented.");
  }

  /**
   * True if \`_constraint_\` is empty.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    // TODO: Implement any_allowed behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method any_allowed not yet implemented.");
  }

  /**
   * True if \`_other.any_allowed_\` or else every constraint in the \`_constraint_\` list exists in the \`_other.constraint_\`, and \`_effective_constraint_status()_\` is <= \`_other.effective_constraint_status()_\`.
   * @param other - Parameter
   * @returns Result value
   */
  c_value_conforms_to(other: C_TERMINOLOGY_CODE): openehr_base.Boolean {
    // TODO: Implement c_value_conforms_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_value_conforms_to not yet implemented.");
  }

  /**
   * True if \`_constraint_\` and \`_other.constraint_\` are both value-set ids, and expand to identical value sets, or else are identical value codes; and \`_effective_constraint_status()_\` = \`_other.effective_constraint_status()_\`.
   * @param other - Parameter
   * @returns Result value
   */
  c_value_congruent_to(other: C_TERMINOLOGY_CODE): openehr_base.Boolean {
    // TODO: Implement c_value_congruent_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_value_congruent_to not yet implemented.");
  }
}

/**
 * Purpose Abstract parent of \`C_ORDERED\` types whose base type is an ISO date/time type.
 */
export abstract class C_TEMPORAL<T extends openehr_base.Temporal>
  extends C_ORDERED<T> {
  /**
   * Internal storage for pattern_constraint
   * @protected
   */
  protected _pattern_constraint?: openehr_base.String;

  /**
   * Optional alternative constraint in the form of a pattern based on ISO8601. See descendants for details.
   */
  get pattern_constraint(): string | undefined {
    return this._pattern_constraint?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for pattern_constraint.
   * Use this to access openehr_base.String methods.
   */
  get $pattern_constraint(): openehr_base.String | undefined {
    return this._pattern_constraint;
  }

  /**
   * Sets pattern_constraint from either a primitive value or openehr_base.String wrapper.
   */
  set pattern_constraint(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._pattern_constraint = undefined;
    } else if (typeof val === "string") {
      this._pattern_constraint = openehr_base.String.from(val);
    } else {
      this._pattern_constraint = val;
    }
  }

  /**
   * True if \`_a_pattern_\` is a valid constraint. Define in concrete descendants.
   * @param a_pattern - Parameter
   * @returns Result value
   */
  abstract valid_pattern_constraint(
    a_pattern: openehr_base.String,
  ): openehr_base.Boolean;

  /**
   * Return True if \`_a_pattern_\` can be replaced by \`_an_other_pattern_\` in a specialised constraint. Define in concrete subtypes.
   * @param a_pattern - Parameter
   * @param an_other_pattern - Parameter
   * @returns Result value
   */
  abstract valid_pattern_constraint_replacement(
    a_pattern: openehr_base.String,
    an_other_pattern: openehr_base.String,
  ): openehr_base.Boolean;

  /**
   * True if any value (i.e. instance) of the reference model type would be allowed. Redefined in descendants.
   * @returns Result value
   */
  any_allowed(): openehr_base.Boolean {
    // TODO: Implement any_allowed behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method any_allowed not yet implemented.");
  }

  /**
   * True if \`precursor()\` or else \`_other.pattern_constraint_\` is empty, or else \`_pattern_constraint_\` is a valid (narrower) replacement for \`_other.pattern_constraint_\`.
   * @param other - Parameter
   * @returns Result value
   */
  c_value_conforms_to(_other: C_ORDERED<T>): openehr_base.Boolean {
    // TODO: Implement c_value_conforms_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_value_conforms_to not yet implemented.");
  }

  /**
   * True if \`precursor (other)\` and \`_pattern_constraint_ ~ _other.pattern_constraint_\`, i.e. either both \`Void\` or else both non-\`Void\` and identical.
   * @param other - Parameter
   * @returns Result value
   */
  c_value_congruent_to(_other: C_ORDERED<T>): openehr_base.Boolean {
    // TODO: Implement c_value_congruent_to behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method c_value_congruent_to not yet implemented.");
  }
}

/**
 * ISO 8601-compatible constraint on instances of Time. There is no validity flag for ‘hour’, since it must always be by definition mandatory in order to have a sensible time at all. Syntax expressions of instances of this class include “HH:??:xx” (time with optional minutes and seconds not allowed).
 */
export class C_TIME extends C_PRIMITIVE {
  /**
   * Validity of minute in constrained time.
   */
  minute_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of second in constrained time.
   */
  second_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of millisecond in constrained time.
   */
  millisecond_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of timezone in constrained date.
   */
  timezone_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Interval of Times specifying constraint.
   */
  range?: undefined;
  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Iso8601_time = undefined;
  /**
   * True if validity is in the form of a range; useful for developers to check which kind of constraint has been set.
   * @returns Result value
   */
  validity_is_range(): openehr_base.Boolean {
    // TODO: Implement validity_is_range behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method validity_is_range not yet implemented.");
  }
}

/**
 * ISO 8601-compatible constraint on instances of Date in the form either of a set of validity values, or an actual date range. There is no validity flag for ‘year’, since it must always be by definition mandatory in order to have a sensible date at all. Syntax expressions of instances of this class include “YYYY-??-??” (date with optional month and day).
 */
export class C_DATE extends C_PRIMITIVE {
  /**
   * Validity of day in constrained date.
   */
  day_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of month in constrained date.
   */
  month_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of timezone in constrained date.
   */
  timezone_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Interval of Dates specifying constraint.
   */
  range?: undefined;
  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Iso8601_date = undefined;
}

/**
 * ISO 8601-compatible constraint on instances of Date_Time. There is no validity flag for ‘year’, since it must always be by definition mandatory in order to have a sensible date/time at all. Syntax expressions of instances of this class include “YYYY-MM-DDT??:??:??” (date/time with optional time) and “YYYY-MMDDTHH:MM:xx” (date/time, seconds not allowed).
 */
export class C_DATE_TIME extends C_PRIMITIVE {
  /**
   * Validity of month in constrained date.
   */
  month_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of day in constrained date.
   */
  day_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of hour in constrained time.
   */
  hour_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of minute in constrained time.
   */
  minute_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of second in constrained time.
   */
  second_validity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of millisecond in constrained time.
   */
  millisecond_valdity?: openehr_base.VALIDITY_KIND;
  /**
   * Validity of timezone in constrained date.
   */
  timezone_valdity?: openehr_base.VALIDITY_KIND;
  /**
   * Range of Date_times specifying constraint.
   */
  range?: undefined;
  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Iso8601_date_time = undefined;
  /**
   * True if validity is in the form of a range; useful for developers to check which kind of constraint has been set.
   * @returns Result value
   */
  validity_is_range(): openehr_base.Boolean {
    // TODO: Implement validity_is_range behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error("Method validity_is_range not yet implemented.");
  }
}

/**
 * ISO 8601-compatible constraint on instances of Duration. In ISO 8601 terms, constraints might are of the form “PWD” (weeks and/or days), “PDTHMS” (days, hours, minutes, seconds) and so on.
 *
 * Both range and the constraint pattern can be set at the same time, corresponding to the ADL constraint \`"PWD/|P0W..P50W|"\`.
 *
 * As for all of openEHR, two ISO 8601 exceptions are allowed:
 *
 * * the ‘W’ (week) designator can be mixed in - the allowed patterns are: \`P[Y|y][M|m][D|d][T[H|h][M|m][S|s]]\` and \`P[W|w]\`;
 * * the values used in an interval constraint may be negated, i.e. a leading minus (\`'-'\`) sign may be used.
 */
export class C_DURATION extends C_PRIMITIVE {
  /**
   * Internal storage for years_allowed
   * @protected
   */
  protected _years_allowed?: openehr_base.Boolean;

  get years_allowed(): boolean | undefined {
    return this._years_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for years_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $years_allowed(): openehr_base.Boolean | undefined {
    return this._years_allowed;
  }

  /**
   * Sets years_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set years_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._years_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._years_allowed = openehr_base.Boolean.from(val);
    } else {
      this._years_allowed = val;
    }
  }

  /**
   * Internal storage for months_allowed
   * @protected
   */
  protected _months_allowed?: openehr_base.Boolean;

  /**
   * True if months are allowed in the constrained Duration.
   */
  get months_allowed(): boolean | undefined {
    return this._months_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for months_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $months_allowed(): openehr_base.Boolean | undefined {
    return this._months_allowed;
  }

  /**
   * Sets months_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set months_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._months_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._months_allowed = openehr_base.Boolean.from(val);
    } else {
      this._months_allowed = val;
    }
  }

  /**
   * Internal storage for weeks_allowed
   * @protected
   */
  protected _weeks_allowed?: openehr_base.Boolean;

  get weeks_allowed(): boolean | undefined {
    return this._weeks_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for weeks_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $weeks_allowed(): openehr_base.Boolean | undefined {
    return this._weeks_allowed;
  }

  /**
   * Sets weeks_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set weeks_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._weeks_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._weeks_allowed = openehr_base.Boolean.from(val);
    } else {
      this._weeks_allowed = val;
    }
  }

  /**
   * Internal storage for days_allowed
   * @protected
   */
  protected _days_allowed?: openehr_base.Boolean;

  /**
   * True if days are allowed in the constrained Duration.
   */
  get days_allowed(): boolean | undefined {
    return this._days_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for days_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $days_allowed(): openehr_base.Boolean | undefined {
    return this._days_allowed;
  }

  /**
   * Sets days_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set days_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._days_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._days_allowed = openehr_base.Boolean.from(val);
    } else {
      this._days_allowed = val;
    }
  }

  /**
   * Internal storage for hours_allowed
   * @protected
   */
  protected _hours_allowed?: openehr_base.Boolean;

  /**
   * True if hours are allowed in the constrained Duration.
   */
  get hours_allowed(): boolean | undefined {
    return this._hours_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for hours_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $hours_allowed(): openehr_base.Boolean | undefined {
    return this._hours_allowed;
  }

  /**
   * Sets hours_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set hours_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._hours_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._hours_allowed = openehr_base.Boolean.from(val);
    } else {
      this._hours_allowed = val;
    }
  }

  /**
   * Internal storage for minutes_allowed
   * @protected
   */
  protected _minutes_allowed?: openehr_base.Boolean;

  /**
   * True if minutes are allowed in the constrained Duration.
   */
  get minutes_allowed(): boolean | undefined {
    return this._minutes_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for minutes_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $minutes_allowed(): openehr_base.Boolean | undefined {
    return this._minutes_allowed;
  }

  /**
   * Sets minutes_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set minutes_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._minutes_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._minutes_allowed = openehr_base.Boolean.from(val);
    } else {
      this._minutes_allowed = val;
    }
  }

  /**
   * Internal storage for seconds_allowed
   * @protected
   */
  protected _seconds_allowed?: openehr_base.Boolean;

  get seconds_allowed(): boolean | undefined {
    return this._seconds_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for seconds_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $seconds_allowed(): openehr_base.Boolean | undefined {
    return this._seconds_allowed;
  }

  /**
   * Sets seconds_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set seconds_allowed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._seconds_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._seconds_allowed = openehr_base.Boolean.from(val);
    } else {
      this._seconds_allowed = val;
    }
  }

  /**
   * Internal storage for fractional_seconds_allowed
   * @protected
   */
  protected _fractional_seconds_allowed?: openehr_base.Boolean;

  /**
   * True if fractional seconds are allowed in the constrained Duration.
   */
  get fractional_seconds_allowed(): boolean | undefined {
    return this._fractional_seconds_allowed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for fractional_seconds_allowed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $fractional_seconds_allowed(): openehr_base.Boolean | undefined {
    return this._fractional_seconds_allowed;
  }

  /**
   * Sets fractional_seconds_allowed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set fractional_seconds_allowed(
    val: boolean | openehr_base.Boolean | undefined,
  ) {
    if (val === undefined || val === null) {
      this._fractional_seconds_allowed = undefined;
    } else if (typeof val === "boolean") {
      this._fractional_seconds_allowed = openehr_base.Boolean.from(val);
    } else {
      this._fractional_seconds_allowed = val;
    }
  }

  /**
   * Range of Durations specifying constraint.
   */
  range?: undefined;
  /**
   * The value to assume if this item is not included in data, due to being part of an optional structure.
   */
  override assumed_value?: openehr_base.Iso8601_duration = undefined;
}

/**
 * Definitions related to \`C_TEMPORAL\` constraints.
 */
export class C_TEMPORAL_DEFINITIONS {
  /**
   * List of allowed date constraints. Contains the values:
   *
   * \`\`\`
   * "YYYY-MM-DD"    -- full date required
   * "YYYY-MM-??"    -- day optional
   * "YYYY-MM-XX"    -- day prohibited
   * "YYYY-??-??"    -- any partial or full date
   * "YYYY-??-XX"    -- day prohibited
   * "YYYY-XX-XX"    -- only prohibited
   * \`\`\`
   */
  valid_date_constraint_patterns?: undefined;
  /**
   * List of allowed date constraint replacements e.g. in specialised archetype. Contains the values:
   *
   * ----
   * "YYYY-MM-DD": [],
   *
   * "YYYY-MM-??": ["YYYY-MM-DD",
   *                "YYYY-MM-XX"],
   *
   * "YYYY-MM-XX": [],
   *
   * "YYYY-??-??": ["YYYY-MM-??",
   *                "YYYY-MM-DD",
   *                "YYYY-MM-XX",
   *                "YYYY-??-XX",
   *                "YYYY-XX-XX"],
   *
   * "YYYY-??-XX": ["YYYY-MM-XX",
   *               "YYYY-XX-XX"],
   *
   * "YYYY-XX-XX": []
   * ----
   *
   * In the above, an empty list value indicates no replacements possible.
   */
  valid_date_constraint_replacements?: undefined;
  /**
   * List of allowed time constraints. Contains the values:
   *
   * \`\`\`
   * "HH:MM:SS"    -- full time required
   * "HH:MM:??"    -- seconds optional
   * "HH:MM:XX"    -- minutes required, seconds prohibited
   * "HH:??:??"    -- minutes and seconds optional
   * "HH:??:XX"    -- minutes optional, seconds prohibited
   * \`\`\`
   */
  valid_time_constraint_patterns?: undefined;
  /**
   * List of allowed time constraint replacements e.g. in specialised archetype. Contains the values:
   *
   * ----
   * "HH:MM:SS": [],
   *
   * "HH:MM:??": ["HH:MM:SS",
   *              "HH:MM:XX"],
   *
   * "HH:MM:XX": [],
   *
   * "HH-??-??": ["HH:MM:??",
   *              "HH:MM:SS",
   *              "HH:MM:XX",
   *              "HH:??:XX"],
   *
   * "HH-??-XX": ["HH:MM:XX"]
   * ----
   *
   * In the above, an empty list value indicates no replacements possible.
   */
  valid_time_constraint_replacements?: undefined;
  /**
   * List of allowed date/time constraints. Contains the values:
   *
   * \`\`\`
   * "YYYY-MM-DDTHH:MM:SS"    -- full date/time required
   * "YYYY-MM-DDTHH:MM:??"    -- seconds optional
   * "YYYY-MM-DDTHH:MM:XX"    -- seconds prohibited
   * "YYYY-MM-DDTHH:??:??"    -- minutes and seconds optional
   * "YYYY-MM-DDTHH:??:XX"    -- minutes optional, seconds prohibited
   * "YYYY-??-??T??:??:??"    -- any date/time ok
   * \`\`\`
   */
  valid_date_time_constraint_patterns?: undefined;
  /**
   * List of allowed date/time constraint replacements e.g. in specialised archetype. Contains the values:
   *
   * ----
   * "YYYY-MM-DDTHH:MM:SS": [],
   *
   * "YYYY-MM-DDTHH:MM:??": ["YYYY-MM-DDTHH:MM:SS",
   *                         "YYYY-MM-DDTHH:MM:XX"],
   *
   * "YYYY-MM-DDTHH:MM:XX": [],
   *
   * "YYYY-MM-DDTHH:??:??": ["YYYY-MM-DDTHH:??:XX",
   *                         "YYYY-MM-DDTHH:MM:SS",
   *                         "YYYY-MM-DDTHH:MM:??",
   *                         "YYYY-MM-DDTHH:MM:XX"],
   *
   * "YYYY-MM-DDTHH:??:XX": ["YYYY-MM-DDTHH:MM:XX"],
   *
   * "YYYY-??-??T??:??:??": ["YYYY-MM-DDTHH:MM:SS",
   *                         "YYYY-MM-DDTHH:MM:??",
   *                         "YYYY-MM-DDTHH:MM:XX",
   *                         "YYYY-MM-DDTHH:??:??",
   *                         "YYYY-MM-DDTHH:??:XX"]
   * ----
   */
  valid_date_time_constraint_replacements?: undefined;
  /**
   * True if ISO8601 duration string \`_other_dur_\` contains every character element in \`_a_dur_\`. For example: 'PYD' (period with year and days only) conforms to 'PYMD', but doesn't conform to 'PY'.
   * @param a_dur - Parameter
   * @param other_dur - Parameter
   * @returns Result value
   */
  valid_duration_constraint_replacement(
    a_dur: openehr_base.String,
    other_dur: openehr_base.String,
  ): openehr_base.Boolean {
    // TODO: Implement valid_duration_constraint_replacement behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error(
      "Method valid_duration_constraint_replacement not yet implemented.",
    );
  }

  /**
   * True if string literal like \`"yyyy-MM-dd"\` containing \`"XX"\` or \`"??"\` in \`MM\` or \`dd\` slots in pattern constraint is in \`_valid_date_constraint_patterns_\`.
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_date_constraint_pattern(
    s: openehr_base.String,
  ): openehr_base.Boolean {
    // TODO: Implement valid_iso8601_date_constraint_pattern behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error(
      "Method valid_iso8601_date_constraint_pattern not yet implemented.",
    );
  }

  /**
   * True if string literal like \`"hh:mm:ss[.ssss]"\` containing \`"XX"\` or \`"??"\` in \`mm\` or \`ss\` slots in pattern constraint is in \`_valid_time_constraint_patterns_\`.
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_time_constraint_pattern(
    s: openehr_base.String,
  ): openehr_base.Boolean {
    // TODO: Implement valid_iso8601_time_constraint_pattern behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error(
      "Method valid_iso8601_time_constraint_pattern not yet implemented.",
    );
  }

  /**
   * True if string literal in form \`"yyyy-MM-dd hh:mm:ss[.ssss]"\` containing \`"XX"\` or \`"??"\` in variable slots in pattern constraint is in \`_valid_date_time_constraint_patterns_\`.
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_date_time_constraint_pattern(
    s: openehr_base.String,
  ): openehr_base.Boolean {
    // TODO: Implement valid_iso8601_date_time_constraint_pattern behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error(
      "Method valid_iso8601_date_time_constraint_pattern not yet implemented.",
    );
  }

  /**
   * True if string in form \`P[Y|y][M|m][W|w][D|d][T[H|h][M|m][S|s]]\` (note: allowing 'W' to be mixed in is an openEHR deviation of ISO 8601).
   * @param s - Parameter
   * @returns Result value
   */
  valid_iso8601_duration_constraint_pattern(
    s: openehr_base.String,
  ): openehr_base.Boolean {
    // TODO: Implement valid_iso8601_duration_constraint_pattern behavior
    // This will be covered in Phase 3 (see ROADMAP.md)
    throw new Error(
      "Method valid_iso8601_duration_constraint_pattern not yet implemented.",
    );
  }
}

/**
 * Status of \`_constraint_\`, with values allowing for 'soft' constraints, which are effectively different kinds of suggestions.
 */
export class CONSTRAINT_STATUS extends openehr_base.Integer {
}
